import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCheckPromoCodesMutation,
  useErpCareerGrowthRulesQuery,
  useErpCareerShareTemplatesQuery,
  useSaveShareTemplateMutation,
  useDropPromoCodeMutation,
  useGivePromoCodeMutation,
  useErpCareerPromoCampaignsQuery,
  useErpCareerPromoCodesQuery,
  useSavePromoCampaignMutation,
  useTogglePromoCampaignMutation,
} from 'core/graphql';

import {
  IPromoCampaign,
  IPromoCodeRow,
  ISkillMutationResult,
} from './careerModel';

const EMPTY_CAMPAIGNS: IPromoCampaign[] = [];
const EMPTY_CODES: IPromoCodeRow[] = [];

/** ТЗ 0039 шаг 6 — акции. */
export const usePromoCampaigns = (includeInactive = true) => {
  const { data, loading, refetch } = useErpCareerPromoCampaignsQuery({
    variables: { includeInactive },
    fetchPolicy: 'cache-and-network',
  });

  return {
    campaigns: data?.careerPromoCampaigns ?? EMPTY_CAMPAIGNS,
    loading,
    refetch,
  };
};

/** ТЗ 0039 шаг 6 — выданные коды. */
export const usePromoCodes = (
  campaignId?: string,
  status?: string,
  search?: string,
) => {
  const { data, loading, refetch } = useErpCareerPromoCodesQuery({
    variables: {
      campaignId: campaignId || undefined,
      status: status || undefined,
      search: search || undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  return {
    codes: data?.careerPromoCodes ?? EMPTY_CODES,
    loading,
    refetch,
  };
};

/** Мутации акций и кодов. Удаления акции нет: коды уже у людей на руках. */
export const usePromoMutations = () => {
  const [save, saveState] = useSavePromoCampaignMutation();
  const [toggle] = useTogglePromoCampaignMutation();
  const [revoke] = useDropPromoCodeMutation();
  const [give, giveState] = useGivePromoCodeMutation();
  const [reconcile, reconcileState] = useCheckPromoCodesMutation();

  const asResult = (
    payload:
      | {
          success?: boolean | null;
          customErrors?:
            | ({ field?: string | null; message?: string | null } | null)[]
            | null;
        }
      | null
      | undefined,
  ): ISkillMutationResult => ({
    success: Boolean(payload?.success),
    errors: (payload?.customErrors ?? []).map((item) => ({
      field: item?.field ?? '',
      message: item?.message ?? '',
    })),
  });

  return {
    saveCampaign: useCallback(
      async (input: Record<string, unknown>) => {
        // 🔴 `JSONString` на проводе — это СТРОКА. Объект graphene отбивает с
        // «the JSON object must be str, bytes or bytearray», причём в виде
        // 400 на кнопке, то есть пользователь видит белый экран, а не ошибку
        // поля. Сериализуем ЗДЕСЬ, чтобы форма про кодировку не знала.
        const { data } = await save({
          variables: {
            input: {
              ...input,
              ruleParams: JSON.stringify(input.ruleParams ?? {}),
            },
          } as never,
        });
        return asResult(data?.upsertPromoCampaign);
      },
      [save],
    ),
    toggleCampaign: useCallback(
      async (id: string, isActive: boolean) => {
        const { data } = await toggle({ variables: { id, isActive } });
        return asResult(data?.setPromoCampaignActive);
      },
      [toggle],
    ),
    revokeCode: useCallback(
      async (id: string, reason: string) => {
        const { data } = await revoke({ variables: { id, reason } });
        return asResult(data?.revokePromoCode);
      },
      [revoke],
    ),
    /**
     * Выдать код студенту руками — в обход правила акции.
     *
     * Возвращает сам код: оператор диктует его студенту в переписке, и
     * заставлять его после этого искать строку в таблице значит добавлять шаг
     * ради ничего.
     */
    giveCode: useCallback(
      async (campaignId: string, studentId: string) => {
        const { data } = await give({ variables: { campaignId, studentId } });
        return {
          ...asResult(data?.issuePromoCode),
          code: data?.issuePromoCode?.code ?? '',
        };
      },
      [give],
    ),
    giving: giveState.loading,
    /**
     * «Проверить сейчас» — та же сверка, что идёт утром задачей.
     * Возвращает сводку, чтобы оператор видел, что именно она сделала:
     * кнопка без ответа читается как «ничего не произошло».
     */
    reconcileNow: useCallback(async () => {
      const { data } = await reconcile();
      const payload = data?.reconcilePromoCodes;
      return {
        success: Boolean(payload?.success),
        checked: payload?.checked ?? 0,
        redeemed: payload?.redeemed ?? 0,
        ambiguous: payload?.ambiguous ?? 0,
        expired: payload?.expired ?? 0,
      };
    }, [reconcile]),
    pending: saveState.loading || reconcileState.loading,
  };
};

/**
 * ТЗ 0039 доводка — словарь правил выдачи для формы акции.
 *
 * Приходит с сервера по той же причине, что и словарь конструктора анкет:
 * копия списка в TypeScript однажды разойдётся с реестром, и форма предложит
 * правило, которого сервер не знает.
 */
export const useGrowthRules = () => {
  const { t } = useTranslation();
  const { data, loading } = useErpCareerGrowthRulesQuery({
    fetchPolicy: 'cache-first',
  });

  // Подписи локализуем ЗДЕСЬ: сервер отдаёт их по-русски, а ERP у владельца
  // работает на польском — иначе в форме акции половина экрана русская.
  // Серверная подпись остаётся запасной: новое правило без ключа покажется
  // по-русски, а не путём ключа i18n.
  const rules = useMemo(
    () =>
      (data?.careerGrowthRules ?? []).map((row) => ({
        ...row,
        label: t(`pages.careerCenter.promos.rules.${row.value}`, {
          defaultValue: row.label,
        }),
      })),
    [data, t],
  );

  return { rules, loading };
};

/** ТЗ 0039 доводка — тексты постов (канал × язык). */
export const useShareTemplates = () => {
  const { data, loading, refetch } = useErpCareerShareTemplatesQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [save, { loading: saving }] = useSaveShareTemplateMutation();

  const saveTemplate = useCallback(
    async (input: {
      channel: string;
      language: string;
      text: string;
      targetUrl?: string;
      isActive?: boolean;
    }) => {
      const { data: result } = await save({ variables: { input } });
      const ok = Boolean(result?.upsertShareTemplate?.success);
      if (ok) await refetch();
      return ok;
    },
    [save, refetch],
  );

  return {
    templates: data?.careerShareTemplates ?? [],
    loading,
    saving,
    saveTemplate,
  };
};
